// src/jsx/pages/reports/GpIncomeExpenseReportPage.jsx

import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Pagination,
  Spinner,
} from "react-bootstrap";
import {
  Download,
  Search,
  RefreshCcw,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { KANNADA_FONT_BASE64 } from "../../../utils/kannadaFontBase64";
import "./ReportPage.css";

import {
  fetchGpIncomeExpenseReportThunk,
  clearGpIncomeExpenseReport,
} from "../../../store/slices/gpIncomeExpenseReportSlice";

const BASE_TITLE = "ಗ್ರಾಮ ಪಂಚಾಯತ್‌ವಾರು ತ್ಯಾಜ್ಯ ನಿರ್ವಹಣೆ ಆದಾಯ–ವೆಚ್ಚ ವರದಿ";

const fmt = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n);
  return x.toLocaleString("en-IN");
};

const getToday = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getFirstDayOfCurrentMonth = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}-01`;
};

const formatKannadaFullDate = (dateStr) => {
  if (!dateStr) return "";

  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;

  const months = [
    "ಜನವರಿ",
    "ಫೆಬ್ರವರಿ",
    "ಮಾರ್ಚ್",
    "ಏಪ್ರಿಲ್",
    "ಮೇ",
    "ಜೂನ್",
    "ಜುಲೈ",
    "ಆಗಸ್ಟ್",
    "ಸೆಪ್ಟೆಂಬರ್",
    "ಅಕ್ಟೋಬರ್",
    "ನವೆಂಬರ್",
    "ಡಿಸೆಂಬರ್",
  ];

  const day = String(d.getDate()).padStart(2, "0");
  return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const formatSelectedDateRangeKannada = (fromDate, toDate) => {
  if (fromDate && toDate) {
    return `${formatKannadaFullDate(fromDate)} ರಿಂದ ${formatKannadaFullDate(
      toDate
    )}`;
  }
  if (fromDate) return `${formatKannadaFullDate(fromDate)} ರಿಂದ`;
  if (toDate) return `${formatKannadaFullDate(toDate)} ವರೆಗೆ`;
  return "";
};

const getKannadaMonthYear = (dateStr) => {
  if (!dateStr) return "";

  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";

  const months = [
    "ಜನವರಿ",
    "ಫೆಬ್ರವರಿ",
    "ಮಾರ್ಚ್",
    "ಏಪ್ರಿಲ್",
    "ಮೇ",
    "ಜೂನ್",
    "ಜುಲೈ",
    "ಆಗಸ್ಟ್",
    "ಸೆಪ್ಟೆಂಬರ್",
    "ಅಕ್ಟೋಬರ್",
    "ನವೆಂಬರ್",
    "ಡಿಸೆಂಬರ್",
  ];

  return `${months[d.getMonth()]} ${d.getFullYear()}`;
};

export default function GpIncomeExpenseReportPage() {
  const dispatch = useDispatch();

  const { rows = [], loading = false, error = null } = useSelector(
    (s) => s.gpIncomeExpenseReport || {}
  );

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [fromDate, setFromDate] = useState(getFirstDayOfCurrentMonth());
  const [toDate, setToDate] = useState(getToday());

  const selectedDateLabel = formatSelectedDateRangeKannada(fromDate, toDate);

  const reportTitle = selectedDateLabel
    ? `${BASE_TITLE} (${selectedDateLabel})`
    : BASE_TITLE;

  const openingLabel = fromDate
    ? `${getKannadaMonthYear(fromDate)} ಆರಂಭಿಕ ಶಿಲ್ಕ`
    : "ಆರಂಭಿಕ ಶಿಲ್ಕ";

  const closingLabel = toDate
    ? `${getKannadaMonthYear(toDate)} ಅಂತಿಮ ಶಿಲ್ಕ`
    : "ಅಂತಿಮ ಶಿಲ್ಕ";

  const pdfOpeningLabel = fromDate
    ? `${getKannadaMonthYear(fromDate)}\nಆರಂಭಿಕ ಶಿಲ್ಕ`
    : "ಆರಂಭಿಕ\nಶಿಲ್ಕ";

  const pdfClosingLabel = toDate
    ? `${getKannadaMonthYear(toDate)}\nಅಂತಿಮ ಶಿಲ್ಕ`
    : "ಅಂತಿಮ\nಶಿಲ್ಕ";

  useEffect(() => {
    dispatch(
      fetchGpIncomeExpenseReportThunk({
        from_date: getFirstDayOfCurrentMonth(),
        to_date: getToday(),
      })
    );
  }, [dispatch]);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, rows.length]);

  const handleSort = (field) => {
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) =>
          currentDirection === "asc" ? "desc" : "asc"
        );

        return currentField;
      }

      setSortDirection("asc");
      return field;
    });

    setPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }

    return sortDirection === "asc" ? (
      <ChevronUp size={14} className="ms-1" />
    ) : (
      <ChevronDown size={14} className="ms-1" />
    );
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    const dataRows = (rows || []).filter((r) => r.taluk !== "ಒಟ್ಟು");
    const totalRow = (rows || []).find((r) => r.taluk === "ಒಟ್ಟು");

    const searchedRows = !q
      ? [...dataRows]
      : dataRows.filter((r) => {
          const searchableText = [
            r.taluk,
            r.gp_name,
            r.opening_balance,
            r.user_fee_income,
            r.recyclable_sale_income,
            r.wet_sale_income,
            r.grant_income,
            r.other_income,
            r.total_income,
            r.salary_expense,
            r.fuel_expense,
            r.nonrecyclable_disposal_expense,
            r.other_expense,
            r.total_expense,
            r.closing_balance,
            r.remark,
          ]
            .map((value) => String(value ?? ""))
            .join(" ")
            .toLowerCase();

          return searchableText.includes(q);
        });

    const numericFields = [
      "opening_balance",
      "user_fee_income",
      "recyclable_sale_income",
      "wet_sale_income",
      "grant_income",
      "other_income",
      "total_income",
      "salary_expense",
      "fuel_expense",
      "nonrecyclable_disposal_expense",
      "other_expense",
      "total_expense",
      "closing_balance",
    ];

    const sortedRows = !sortField
      ? searchedRows
      : [...searchedRows].sort((a, b) => {
          let aValue = a?.[sortField];
          let bValue = b?.[sortField];

          if (numericFields.includes(sortField)) {
            aValue = Number(aValue || 0);
            bValue = Number(bValue || 0);

            return sortDirection === "asc"
              ? aValue - bValue
              : bValue - aValue;
          }

          const comparison = String(aValue || "").localeCompare(
            String(bValue || ""),
            undefined,
            {
              numeric: true,
              sensitivity: "base",
            }
          );

          return sortDirection === "asc" ? comparison : -comparison;
        });

    return totalRow ? [...sortedRows, totalRow] : sortedRows;
  }, [rows, search, sortField, sortDirection]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const handleLoad = () => {
    dispatch(
      fetchGpIncomeExpenseReportThunk({
        from_date: fromDate,
        to_date: toDate,
      })
    );
  };

  const clearAll = () => {
    setSearch("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);
    setFromDate(getFirstDayOfCurrentMonth());
    setToDate(getToday());
    dispatch(clearGpIncomeExpenseReport());
  };

  const exportExcel = () => {
    const data = filteredRows.map((r) => ({
      "ತಾಲೂಕು": r.taluk,
      "ಗ್ರಾ.ಪಂ": r.gp_name,
      [`${openingLabel} (₹)`]: r.opening_balance,

      "ಬಳಕೆದಾರರ ಶುಲ್ಕ ಸಂಗ್ರಹದಿಂದ ಬಂದ ಆದಾಯ (₹)": r.user_fee_income,
      "ಒಣ ತ್ಯಾಜ್ಯ ಮಾರಾಟದಿಂದ ಬಂದ ಆದಾಯ (₹)": r.recyclable_sale_income,
      "ಹಸಿ ತ್ಯಾಜ್ಯ ಮಾರಾಟದಿಂದ ಬಂದ ಆದಾಯ (₹)": r.wet_sale_income,
      "ಗ್ರಾಮ ಪಂಚಾಯತ್ ನಿಧಿಯಿಂದ ಭರಿಸಿದ ಮೊತ್ತ (15th / GP own fund / other fund) (₹)":
        r.grant_income,
      "ಇತರೆ ಆದಾಯ (ಬಡ್ಡಿ ಮೊತ್ತ, ದಂಡ, ದೇಣಿಗೆ, ಇತರೆ) (₹)": r.other_income,
      "ಒಟ್ಟು ಆದಾಯ (₹)": r.total_income,

      // "ವಾಹನ ವೆಚ್ಚ (₹)": r.vehicle_expense,
      "ವೇತನ ಪಾವತಿ ವೆಚ್ಚ (₹)": r.salary_expense,
      "ವಾಹನ ಇಂಧನ ಪಾವತಿ ವೆಚ್ಚ (₹)": r.fuel_expense,
      // "ಪಾವತಿ ವೆಚ್ಚ (₹)": r.payment_expense,
      "Non Recyclable ತ್ಯಾಜ್ಯ ವಿಲೇವಾರಿ ವೆಚ್ಚ (₹)":
        r.nonrecyclable_disposal_expense,
      "ಇತರೆ ಖರ್ಚು (ವಾಹನ ರಿಪೇರಿ, PPE kits, Books ಇತ್ಯಾದಿ) (₹)":
        r.other_expense,
      "ಒಟ್ಟು ಖರ್ಚು (₹)": r.total_expense,

      [`${closingLabel} (₹)`]: r.closing_balance,
      "ಸ್ವಂತ / ವಂಡ್ಸ್ / ಬಹುಗ್ರಾ": r.remark,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "gp_swm_income_expense_report.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    doc.addFileToVFS("NotoSansKannada-Regular.ttf", KANNADA_FONT_BASE64);
    doc.addFont("NotoSansKannada-Regular.ttf", "NotoSansKannada", "normal");
    doc.setFont("NotoSansKannada", "normal");

    doc.setFontSize(12);
    doc.text(reportTitle, 40, 40);

    const head = [[
      "ತಾಲೂಕು",
      "ಗ್ರಾ.ಪಂ",
      pdfOpeningLabel,

      "ಬಳಕೆದಾರರ\nಶುಲ್ಕ ಸಂಗ್ರಹದಿಂದ\nಬಂದ ಆದಾಯ",
      "ಒಣ ತ್ಯಾಜ್ಯ\nಮಾರಾಟದಿಂದ\nಬಂದ ಆದಾಯ",
      "ಹಸಿ ತ್ಯಾಜ್ಯ\nಮಾರಾಟದಿಂದ\nಬಂದ ಆದಾಯ",
      "ಗ್ರಾಮ ಪಂಚಾಯತ್\nನಿಧಿಯಿಂದ ಭರಿಸಿದ\nಮೊತ್ತ",
      "ಇತರೆ ಆದಾಯ\n(ಬಡ್ಡಿ, ದಂಡ,\nದೇಣಿಗೆ, ಇತರೆ)",
      "ಒಟ್ಟು ಆದಾಯ",

      // "ವಾಹನ ವೆಚ್ಚ",
      "ವೇತನ ಪಾವತಿ\nವೆಚ್ಚ",
      "ವಾಹನ ಇಂಧನ\nಪಾವತಿ ವೆಚ್ಚ",
      // "ಪಾವತಿ ವೆಚ್ಚ",
      "Non Recyclable\nತ್ಯಾಜ್ಯ ವಿಲೇವಾರಿ\nವೆಚ್ಚ",
      "ಇತರೆ ಖರ್ಚು\n(PPE/Books/\nRepair)",
      "ಒಟ್ಟು ಖರ್ಚು",

      pdfClosingLabel,
      "ಸ್ವಂತ/\nವಂಡ್ಸ್/\nಬಹುಗ್ರಾ",
    ]];

    const body = filteredRows.map((r) => [
      r.taluk || "—",
      r.gp_name || "—",
      fmt(r.opening_balance),

      fmt(r.user_fee_income),
      fmt(r.recyclable_sale_income),
      fmt(r.wet_sale_income),
      fmt(r.grant_income),
      fmt(r.other_income),
      fmt(r.total_income),

      // fmt(r.vehicle_expense),
      fmt(r.salary_expense),
      fmt(r.fuel_expense),
      // fmt(r.payment_expense),
      fmt(r.nonrecyclable_disposal_expense),
      fmt(r.other_expense),
      fmt(r.total_expense),

      fmt(r.closing_balance),
      r.remark || "—",
    ]);

    autoTable(doc, {
      startY: 60,
      head,
      body,
      theme: "grid",
      styles: {
        font: "NotoSansKannada",
        fontStyle: "normal",
        fontSize: 7,
        cellPadding: 3,
        overflow: "linebreak",
        valign: "middle",
        lineColor: 200,
        lineWidth: 0.5,
      },
      headStyles: {
        font: "NotoSansKannada",
        fontStyle: "normal",
        fontSize: 7,
        halign: "center",
        valign: "middle",
      },
      bodyStyles: {
        font: "NotoSansKannada",
        fontStyle: "normal",
      },
      didParseCell: (data) => {
        const rowTaluk = data.row?.raw?.[0];
        if (rowTaluk === "ಒಟ್ಟು") data.cell.styles.fontStyle = "bold";
      },
      tableWidth: "auto",
      margin: { left: 20, right: 20 },
      showHead: "everyPage",
    });

    doc.save("gp_swm_income_expense_report.pdf");
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      />
    );

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    );

    return <Pagination className="mb-0">{items}</Pagination>;
  };

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">{reportTitle}</div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? <div className="alert alert-danger mb-3">{error}</div> : null}

          <Row className="g-3 align-items-end mb-3">
            <Col lg={3} md={6}>
              <Form.Label className="report-label">From Date</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Col>

            <Col lg={3} md={6}>
              <Form.Label className="report-label">To Date</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Col>

            <Col lg={6} md={12} className="d-flex gap-2">
              <Button
                className="report-btn"
                variant="primary"
                onClick={handleLoad}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Load
                  </>
                ) : (
                  "Load"
                )}
              </Button>

              <Button
                className="report-btn"
                variant="outline-secondary"
                onClick={clearAll}
              >
                <RefreshCcw size={16} className="me-2" />
                Clear
              </Button>
            </Col>
          </Row>

          <Row className="g-3 align-items-end mb-3">
            <Col lg={6} md={8}>
              <Form.Label className="report-label">ಹುಡುಕು</Form.Label>
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="ತಾಲೂಕು / ಗ್ರಾಮ ಪಂಚಾಯತ್ / ಮೊತ್ತ..."
                />
              </InputGroup>
            </Col>

            <Col lg={6} md={4} className="d-flex justify-content-end gap-2">
              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={exportExcel}
                disabled={!filteredRows.length}
              >
                <Download size={16} className="me-2" />
                ಎಕ್ಸೆಲ್
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={exportPDF}
                disabled={!filteredRows.length}
              >
                <Download size={16} className="me-2" />
                ಪಿಡಿಎಫ್
              </Button>
            </Col>
          </Row>

          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table hover className="mb-0 report-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 110 }}>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "taluk" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("taluk")}
                      >
                        ತಾಲೂಕು
                        {renderSortIcon("taluk")}
                      </button>
                    </th>

                    <th style={{ minWidth: 170 }}>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "gp_name" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("gp_name")}
                      >
                        ಗ್ರಾ.ಪಂ
                        {renderSortIcon("gp_name")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "opening_balance" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("opening_balance")}
                      >
                        {openingLabel}
                        {renderSortIcon("opening_balance")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "user_fee_income" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("user_fee_income")}
                      >
                        ಬಳಕೆದಾರರ ಶುಲ್ಕ ಸಂಗ್ರಹದಿಂದ ಬಂದ ಆದಾಯ
                        {renderSortIcon("user_fee_income")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "recyclable_sale_income" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("recyclable_sale_income")}
                      >
                        ಒಣ ತ್ಯಾಜ್ಯ ಮಾರಾಟದಿಂದ ಬಂದ ಆದಾಯ
                        {renderSortIcon("recyclable_sale_income")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "wet_sale_income" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("wet_sale_income")}
                      >
                        ಹಸಿ ತ್ಯಾಜ್ಯ ಮಾರಾಟದಿಂದ ಬಂದ ಆದಾಯ
                        {renderSortIcon("wet_sale_income")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "grant_income" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("grant_income")}
                      >
                        ಗ್ರಾಮ ಪಂಚಾಯತ್ ನಿಧಿಯಿಂದ ಭರಿಸಿದ ಮೊತ್ತ
                        {renderSortIcon("grant_income")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "other_income" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("other_income")}
                      >
                        ಇತರೆ ಆದಾಯ (ಬಡ್ಡಿ ಮೊತ್ತ, ದಂಡ, ದೇಣಿಗೆ, ಇತರೆ)
                        {renderSortIcon("other_income")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "total_income" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("total_income")}
                      >
                        ಒಟ್ಟು ಆದಾಯ
                        {renderSortIcon("total_income")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "salary_expense" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("salary_expense")}
                      >
                        ವೇತನ ಪಾವತಿ ವೆಚ್ಚ
                        {renderSortIcon("salary_expense")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "fuel_expense" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("fuel_expense")}
                      >
                        ವಾಹನ ಇಂಧನ ಪಾವತಿ ವೆಚ್ಚ
                        {renderSortIcon("fuel_expense")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "nonrecyclable_disposal_expense" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("nonrecyclable_disposal_expense")}
                      >
                        Non Recyclable ತ್ಯಾಜ್ಯ ವಿಲೇವಾರಿ ವೆಚ್ಚ
                        {renderSortIcon("nonrecyclable_disposal_expense")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "other_expense" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("other_expense")}
                      >
                        ಇತರೆ ಖರ್ಚು (ವಾಹನ ರಿಪೇರಿ, PPE kits, Books ಇತ್ಯಾದಿ)
                        {renderSortIcon("other_expense")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "total_expense" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("total_expense")}
                      >
                        ಒಟ್ಟು ಖರ್ಚು
                        {renderSortIcon("total_expense")}
                      </button>
                    </th>

                    <th className="text-end">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "closing_balance" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("closing_balance")}
                      >
                        {closingLabel}
                        {renderSortIcon("closing_balance")}
                      </button>
                    </th>

                    <th className="text-center">
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "remark" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("remark")}
                      >
                        ಸ್ವಂತ / ವಂಡ್ಸ್ / ಬಹುಗ್ರಾ
                        {renderSortIcon("remark")}
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={16} className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map((r, i) => {
                      const isTotal = r.taluk === "ಒಟ್ಟು";

                      return (
                        <tr
                          key={`${r.taluk}-${r.gp_name}-${i}`}
                          className={isTotal ? "fw-bold" : ""}
                        >
                          <td>{r.taluk || "—"}</td>
                          <td>
                            <b>{r.gp_name || "—"}</b>
                          </td>
                          <td className="text-end">{fmt(r.opening_balance)}</td>

                          <td className="text-end">{fmt(r.user_fee_income)}</td>
                          <td className="text-end">
                            {fmt(r.recyclable_sale_income)}
                          </td>
                          <td className="text-end">{fmt(r.wet_sale_income)}</td>
                          <td className="text-end">{fmt(r.grant_income)}</td>
                          <td className="text-end">{fmt(r.other_income)}</td>
                          <td className="text-end">{fmt(r.total_income)}</td>

                          {/* <td className="text-end">{fmt(r.vehicle_expense)}</td> */}
                          <td className="text-end">{fmt(r.salary_expense)}</td>
                          <td className="text-end">{fmt(r.fuel_expense)}</td>
                          {/* <td className="text-end">{fmt(r.payment_expense)}</td> */}
                          <td className="text-end">
                            {fmt(r.nonrecyclable_disposal_expense)}
                          </td>
                          <td className="text-end">{fmt(r.other_expense)}</td>
                          <td className="text-end">{fmt(r.total_expense)}</td>

                          <td className="text-end">{fmt(r.closing_balance)}</td>
                          <td className="text-center">{r.remark || "—"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={16} className="text-center py-4">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>Rows:</span>
                <Form.Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="page-size-select"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="report-pagination">{renderPagination()}</div>

              <div className="report-page-info">
                Page {currentPage} / {totalPages} • Total {total}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}